import React from "react"
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { X } from "lucide-react"

export interface MultiSelectProps {
  options: Array<{ value: string | number; label: string }>
  value: (string | number)[]
  onChange: (value: (string | number)[]) => void
  label?: string
  helperText?: string
  error?: boolean
  showChips?: boolean
  placeholder?: string
  fullWidth?: boolean
  id?: string
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options = [],
  value = [],
  onChange,
  label,
  helperText,
  error,
  showChips = true,
  placeholder = "Select...",
  fullWidth = true,
  id,
}) => {
  const selectId = id || `multiselect-${label?.toLowerCase().replace(/\s+/g, '-')}`

  const handleValueChange = (newValue: string) => {
    const numValue = Number(newValue)
    const isNumber = !isNaN(numValue) && options.some(opt => opt.value === numValue)
    const actualValue = isNumber ? numValue : newValue

    if (value.includes(actualValue)) {
      onChange(value.filter((v) => v !== actualValue))
    } else {
      onChange([...value, actualValue])
    }
  }

  const handleRemove = (toRemove: string | number, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onChange(value.filter((v) => v !== toRemove))
  }

  const selectedItems = value
    .map((val) => {
      const opt = options.find((o) => o.value === val)
      return opt ? { value: opt.value, label: opt.label } : null
    })
    .filter(Boolean) as { value: string | number; label: string }[]

  return (
    <div className={cn("w-full space-y-2.5", !fullWidth && "w-auto")}>
      {label && (
        <Label htmlFor={selectId} className={cn("mb-1", error && "text-destructive")}>
          {label}
        </Label>
      )}
      {showChips && selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedItems.map((item) => (
            <span
              key={String(item.value)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/10 text-primary text-sm font-medium border border-primary/20"
            >
              {item.label}
              <button
                type="button"
                onClick={(e) => handleRemove(item.value, e)}
                className="p-0.5 rounded hover:bg-primary/20 transition-colors"
                aria-label={`Remove ${item.label}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <ShadcnSelect value={value.length > 0 ? String(value[0]) : ""} onValueChange={handleValueChange}>
        <SelectTrigger
          id={selectId}
          className={cn(
            "min-h-11",
            error && "border-destructive focus:border-destructive focus:ring-destructive/20",
            fullWidth && "w-full"
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => {
            const isSelected = value.includes(option.value)
            return (
              <SelectItem
                key={String(option.value)}
                value={String(option.value)}
                className={cn(isSelected && "bg-accent")}
              >
                {option.label}
                {isSelected && " ✓"}
              </SelectItem>
            )
          })}
        </SelectContent>
      </ShadcnSelect>
      {helperText && (
        <p className={cn("text-xs font-medium", error ? "text-destructive" : "text-muted-foreground")}>
          {helperText}
        </p>
      )}
    </div>
  )
}

export default MultiSelect
